import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon, { type IconName } from "../../../components/AppIcon";
import { useListLeasesQuery } from "../../../store/api/leasesApi";
import { useListMaintenanceRequestsQuery } from "../../../store/api/maintenanceApi";
import { useListPaymentsQuery } from "../../../store/api/paymentsApi";
import { useListViewingsQuery } from "../../../store/api/propertyViewingsApi";
import { useListUsersQuery } from "../../../store/api/usersApi";
import { formatDateTime } from "../../../utils/formatDate";

interface ActivityWithIcon {
	id: string | number;
	type: "payment" | "maintenance" | "lease" | "viewing" | "message" | "tenant";
	title: string;
	description: string;
	user: string;
	timestamp: string;
	icon: IconName;
	color: "primary" | "secondary" | "success" | "warning" | "error";
}

const RecentActivities: React.FC = () => {
	const navigate = useNavigate();

	// Fetch recent data from multiple sources
	const { data: paymentsData } = useListPaymentsQuery({
		page: 1,
		page_size: 5,
		ordering: "-created_at",
	});
	const { data: maintenanceData } = useListMaintenanceRequestsQuery({
		page: 1,
		page_size: 5,
		ordering: "-created_at",
	});
	const { data: leasesData } = useListLeasesQuery({
		page: 1,
		page_size: 5,
		ordering: "-created_at",
	});
	const { data: usersData } = useListUsersQuery({
		page: 1,
		page_size: 5,
		role: "tenant",
		ordering: "-created_at",
	});
	const { data: viewingsData } = useListViewingsQuery({
		page: 1,
		page_size: 10,
		ordering: "-created_at",
	});

	// Combine and sort activities by timestamp
	const activities: ActivityWithIcon[] = useMemo(() => {
		try {
			const allActivities: ActivityWithIcon[] = [];

			// Add payment activities
			paymentsData?.results?.forEach((payment) => {
				if (payment.status === "paid") {
					allActivities.push({
						id: `payment-${payment.id}`,
						type: "payment",
						title: "Payment Received",
						description: `${
							payment.property_name || "Property"
						} - Monthly rent payment processed`,
						user: payment.tenant_name || "Tenant",
						timestamp: payment.paid_date || payment.created_at,
						icon: "CreditCard",
						color: "success",
					});
				}
			});

			// Add maintenance activities
			maintenanceData?.results?.forEach((maintenance) => {
				const isCompleted = maintenance.status === "completed";
				allActivities.push({
					id: `maintenance-${maintenance.id}`,
					type: "maintenance",
					title: isCompleted ? "Work Order Completed" : "Maintenance Request",
					description: `${maintenance.property_name || "Property"} - ${
						maintenance.title
					}`,
					user: maintenance.requested_by_name || "Tenant",
					timestamp: maintenance.completed_date || maintenance.created_at,
					icon: isCompleted ? "CheckCircle" : "Wrench",
					color: isCompleted ? "success" : "warning",
				});
			});

			// Add lease activities
			leasesData?.results?.forEach((lease) => {
				if (lease.status === "active") {
					allActivities.push({
						id: `lease-${lease.id}`,
						type: "lease",
						title: "Lease Signed",
						description: `${
							lease.property_name || "Property"
						} - New lease agreement executed`,
						user: lease.tenant_name || "Tenant",
						timestamp: lease.created_at,
						icon: "FileText",
						color: "primary",
					});
				}
			});

			// Add tenant registration activities
			usersData?.results?.forEach((user) => {
				allActivities.push({
					id: `user-${user.id}`,
					type: "tenant",
					title: "New Tenant Registration",
					description: "Tenant profile created",
					user: `${user.first_name} ${user.last_name}`,
					timestamp: user.created_at,
					icon: "UserPlus",
					color: "primary",
				});
			});

			// Add viewing request activities
			viewingsData?.results?.forEach((viewing) => {
				const isPending = viewing.status === "pending";
				const isConfirmed = viewing.status === "confirmed";
				const isCompleted = viewing.status === "completed";
				const isCancelled = viewing.status === "cancelled";

				let title: string;
				let icon: IconName;
				let color: ActivityWithIcon["color"];

				if (isPending) {
					title = "New Viewing Request";
					icon = "Calendar";
					color = "warning";
				} else if (isConfirmed) {
					title = "Viewing Confirmed";
					icon = "CheckCircle";
					color = "success";
				} else if (isCompleted) {
					title = "Viewing Completed";
					icon = "CheckCircle2";
					color = "success";
				} else if (isCancelled) {
					title = "Viewing Cancelled";
					icon = "XCircle";
					color = "error";
				} else {
					title = "Viewing Scheduled";
					icon = "Calendar";
					color = "primary";
				}

				// Format viewing date for description
				const viewingDate = viewing.viewing_date
					? formatDateTime(viewing.viewing_date)
					: "TBD";

				allActivities.push({
					id: `viewing-${viewing.id}`,
					type: "viewing",
					title,
					description: `${
						viewing.property_name || "Property"
					} - Scheduled for ${viewingDate}`,
					user: viewing.buyer_name || "Guest",
					timestamp: viewing.created_at,
					icon,
					color,
				});
			});

			// Sort by timestamp (newest first) and limit to 10
			return allActivities
				.sort(
					(a, b) =>
						new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
				)
				.slice(0, 10)
				.map((activity) => ({
					...activity,
					timestamp: formatDateTime(activity.timestamp),
				}));
		} catch (error) {
			console.error("Error processing activities:", error);
			return [];
		}
	}, [paymentsData, maintenanceData, leasesData, usersData, viewingsData]);

	const getColorClasses = (color: ActivityWithIcon["color"]): string => {
		const colors = {
			primary: "bg-primary/10 text-primary",
			secondary: "bg-secondary/10 text-secondary",
			success: "bg-success/10 text-success",
			warning: "bg-warning/10 text-warning",
			error: "bg-error/10 text-error",
		};
		return colors[color] || colors.primary;
	};

	return (
		<div className="bg-card border border-border rounded-lg p-6 card-shadow flex flex-col h-full">
			<div className="flex items-center justify-between mb-6 flex-shrink-0">
				<h3 className="text-lg font-semibold text-foreground">
					Recent Activities
				</h3>
				<button
					onClick={() => navigate("/analytics-dashboard")}
					className="text-sm text-primary hover:text-primary/80 smooth-transition"
				>
					View All
				</button>
			</div>

			<div className="space-y-4 flex-1 overflow-y-auto min-h-0">
				{activities.length === 0 ? (
					<div className="text-center py-8 text-muted-foreground">
						No recent activities
					</div>
				) : (
					activities.map((activity) => (
						<div
							key={activity.id}
							className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 smooth-transition"
						>
							<div
								className={`p-2 rounded-full ${getColorClasses(
									activity.color,
								)} flex-shrink-0`}
							>
								<Icon name={activity.icon} size={16} />
							</div>
							<div className="flex-1 min-w-0">
								<div className="flex items-center justify-between mb-1">
									<h4 className="text-sm font-medium text-foreground truncate">
										{activity.title}
									</h4>
									<span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
										{activity.timestamp}
									</span>
								</div>
								<p className="text-sm text-muted-foreground mb-1">
									{activity.description}
								</p>
								<p className="text-xs text-muted-foreground">
									by {activity.user}
								</p>
							</div>
						</div>
					))
				)}
			</div>
		</div>
	);
};

export default RecentActivities;
